import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getI18n } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Stack } from '@mui/material';
import * as Sentry from '@sentry/capacitor';
import { User } from 'firebase/auth';

import {
	personalDetailsManager,
	useBusinessLayer,
	useSpenditToken,
} from '@mopla/business-logic';
import {
	getSocialLoginErrorMessage,
	ISocialLoginResult,
	linkPendingCredential,
	LoginWithApple,
	LoginWithFacebook,
	LoginWithGoogle,
	SocialLoginError,
	SocialLoginErrorModal,
	TSocialLoginMethod,
} from '@mopla/ui';

import { pushAuthEvent } from '../../../app/userTracking/events';
import { useWelcomePageStoreContext } from '../../pages/welcomeV2/useWelcomePageStore';

interface ISocialLoginProps {
	isRegistration: boolean;
	socialLoginMethods: TSocialLoginMethod[];
}

export const SocialLogin: FC<ISocialLoginProps> = ({
	isRegistration,
	socialLoginMethods,
}) => {
	const { t } = useTranslation('socialLogin');
	const { api } = useBusinessLayer();
	const navigate = useNavigate();
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [error, setError] = useState<SocialLoginError | null>(null);
	const { connectTokenToUser } = useSpenditToken();
	const { dispatch } = useWelcomePageStoreContext();

	const handleError = (err: SocialLoginError) => {
		setError(err);
		setShowErrorModal(true);
	};

	const signinHandler = async (signInFn: () => Promise<ISocialLoginResult>) => {
		dispatch({ type: 'SET_IS_LOADING', payload: true });
		let firebaseUser: User | null = null;
		try {
			const { user } = await signInFn();
			firebaseUser = user;
			if (!firebaseUser) {
				throw new Error('No user returned from social login');
			}
		} catch (err) {
			const rawError =
				err instanceof SocialLoginError && err.cause ? err.cause : err;
			dispatch({ type: 'SET_IS_LOADING', payload: false });
			Sentry.addBreadcrumb({
				category: 'auth',
				message: 'Passenger registration flow failed to auth via social login',
			});
			Sentry.captureException(rawError);
			dispatch({ type: 'SET_AUTH_ERROR', payload: true });
			if (err instanceof SocialLoginError) {
				const { title, description } = getSocialLoginErrorMessage(err, t);
				handleError(
					new SocialLoginError(err.code, {
						title,
						description,
					})
				);
			}
			return;
		}

		try {
			// check if user is already registered
			const userData = await api.get('/api/whoami');

			if (userData.userId) {
				await linkPendingCredential();
				if (userData.pleaseComplete) {
					navigate({
						pathname: '/welcome',
						search: 'personalDetails',
					});
				} else {
					personalDetailsManager.set('completed');
					localStorage.removeItem('emailForSignIn');
					return !Capacitor.isNativePlatform()
						? navigate({ pathname: '/welcome', search: 'tryOurApp' })
						: navigate('/home');
				}
			} else {
				throw new Error(
					`Error retrieving user data. Response status: ${userData.status}`
				);
			}
		} catch (whoAmIError: any) {
			if (whoAmIError.response?.status === 401) {
				try {
					const response = await api.post('/api/passengers/create', {
						authenticationProviderId: firebaseUser.uid,
						email: firebaseUser.email,
						language: getI18n().language,
					});

					if (response?.status === 200 || response?.createdUser) {
						connectTokenToUser();

						navigate({
							pathname: '/welcome',
							search: 'personalDetails',
						});
					}
				} catch (createUserError: any) {
					if (createUserError.response?.status === 409) {
						throw new Error(
							'Passenger registration flow failed to auth via social login. User already exists'
						);
					}
				}
			} else {
				Sentry.addBreadcrumb({
					category: 'auth',
					message:
						'Passenger registration flow failed to auth via social login',
				});
				Sentry.captureException(whoAmIError);
				dispatch({ type: 'SET_AUTH_ERROR', payload: true });
			}
		} finally {
			dispatch({ type: 'SET_IS_LOADING', payload: false });
		}
	};

	return (
		<>
			<Stack spacing={2}>
				{socialLoginMethods.includes('google') && (
					<LoginWithGoogle
						onSignIn={signinHandler}
						pushAuthEvent={pushAuthEvent}
						isRegistration={isRegistration}
					/>
				)}
				{socialLoginMethods.includes('apple') && (
					<LoginWithApple
						onSignIn={signinHandler}
						pushAuthEvent={pushAuthEvent}
						isRegistration={isRegistration}
					/>
				)}
				{socialLoginMethods.includes('facebook') && (
					<LoginWithFacebook
						onSignIn={signinHandler}
						pushAuthEvent={pushAuthEvent}
						isRegistration={isRegistration}
					/>
				)}
			</Stack>
			<SocialLoginErrorModal
				isOpen={showErrorModal}
				onClose={() => setShowErrorModal(false)}
				errorTitle={error?.title}
				errorDescription={error?.description}
			/>
		</>
	);
};
